import { ref, onMounted } from '@vue/composition-api';
import {
  SportsApiResponse,
  SportsFrontV2ApiResponse,
  TournamentsApiResponse,
  TournamentPreviewResponse,
  CategoriesApiResponse,
  TournamentsForSportCategoryResponse,
  MarketGroupsResponse,
  MarketGroup,
  EventDetailsRequest,
  EventDetailsResponse,
  LiveEventDetailsResponse
} from '../../types/sport';
import { PlaceBetRequest, PlaceBetResponse } from '../../types/cart';
import store from '../store';

const ROCKY_URL = process.env.VUE_APP_URL_API;

interface ApiResponse {
  data: any;
  status: number;
  message?: string;
}

interface StatsIframeRule {
  type: string;
  scope: string;
  source: number;
  configuration: string;
  status: boolean;
}

interface StatsIframePlatform {
  platformId: number;
  rules: StatsIframeRule[];
}

interface StatsIframesResponse {
  platforms: StatsIframePlatform[];
}

export function useApiV3() {
  type FindEventsPayload = {
    search: string;
    lang?: string;
  };

  const getStatsIframes = async (): Promise<StatsIframesResponse> => {
    try {
      const token = store.getters['settings/getToken'];
      const response = await fetch(`${ROCKY_URL}/skins/stats-iframes`, {
        method: 'GET',
        headers: {
          Authorization: 'Bearer ' + token
        }
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Errore nel recupero delle stats iframes');
      }

      const data: StatsIframesResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching stats iframes:', error);
      throw error;
    }
  };

  type FindEventsResponse = {
    events: any[];
    [key: string]: any;
  };

  const getSportsFrontV2 = async (): Promise<SportsFrontV2ApiResponse> => {
    try {
      const response = await fetch(`${ROCKY_URL}/api/get-sports-front-v2`, {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + store.getters['settings/getToken'],
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          language: store.getters['settings/getLanguage']
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Errore nel recupero sports front v2');
      }

      const data: SportsFrontV2ApiResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching sports front v2:', error);
      throw error;
    }
  };

  const findEvents = async (
    payload: FindEventsPayload
  ): Promise<FindEventsResponse> => {
    try {
      const token = store.getters['settings/getToken'];
      const platformId = store.getters['settings/getPlatformId'];
      const lang = payload.lang || store.getters['settings/getLanguage'];

      const response = await fetch(`${ROCKY_URL}/events/find`, {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          search: payload.search,
          lang: lang,
          platform: platformId
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || 'Errore nella ricerca eventi');
      }

      const data: FindEventsResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Error finding events:', error);
      throw error;
    }
  };

  const getSports = async (): Promise<SportsApiResponse> => {
    try {
      const response = await fetch(`${ROCKY_URL}/api/tree/v3/get-sports`, {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + store.getters['settings/getToken'],
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          filter: 0,
          id_language: store.getters['settings/getLanguage']
        })
      });

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching sports:', error);
      throw error;
    }
  };

  const getCategoriesForSport = async (
    sportId: string
  ): Promise<CategoriesApiResponse> => {
    try {
      const response = await fetch(
        `${ROCKY_URL}/api/tree/v3/get-category-for-sport`,
        {
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + store.getters['settings/getToken'],
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            sportId: sportId,
            filter: 0,
            language: store.getters['settings/getLanguage']
          })
        }
      );

      const data: CategoriesApiResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching categories for sport:', error);
      throw error;
    }
  };

  const getTournamentsForSportCategory = async (
    sportId: string,
    categoryId: string
  ): Promise<TournamentsForSportCategoryResponse> => {
    try {
      const response = await fetch(
        `${ROCKY_URL}/api/tree/v3/get-tournaments-for-sport-category`,
        {
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + store.getters['settings/getToken'],
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            sportId: sportId,
            categoryId: categoryId,
            filter: 0,
            language: store.getters['settings/getLanguage']
          })
        }
      );

      const data: TournamentsForSportCategoryResponse = await response.json();
      return data;
    } catch (error) {
      console.error(
        'Error fetching tournaments for sport and category:',
        error
      );
      throw error;
    }
  };

  const getFeaturedTournaments = async (
    sportId?: string | number
  ): Promise<TournamentsApiResponse> => {
    try {
      const platformId = store.getters['settings/getPlatformId'];
      const language = store.getters['settings/getLanguage'] || 'en';

      // Costruisci l'URL con query parameters
      let url = `${ROCKY_URL}/tournaments/featured/${platformId}?language=${language}`;
      if (sportId != null) {
        url += `&sportId=${sportId.toString()}`;
      }

      const response = await fetch(url, {
        headers: {
          Authorization: 'Bearer ' + store.getters['settings/getToken'],
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching tournaments:', error);
      throw error;
    }
  };

  const getPreviewForTournament = async (
    sportId: string,
    categoryId: string,
    tournamentId: string
  ): Promise<TournamentPreviewResponse> => {
    try {
      const response = await fetch(
        `${ROCKY_URL}/events/getodds/tournament/preview-v3`,
        {
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + store.getters['settings/getToken'],
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            platformId: store.getters['settings/getPlatformId'],
            sportId: sportId,
            categoryId: categoryId,
            tournamentId: tournamentId,
            type: 'previewOdds',
            section: 'sport',
            filter: 0,
            lang: store.getters['settings/getLanguage'],
            forceDefaultGameConsole: false,
            mobilePlatform: false,
            groupOddsWithSpreads: true
          })
        }
      );

      const data = await response.json();
      return data as TournamentPreviewResponse;
    } catch (error) {
      console.error('Error fetching tournament preview:', error);
      throw error;
    }
  };

  const getPreviewForTournamentMarket = async (
    sportId: string,
    categoryId: string,
    tournamentId: string,
    marketId: string,
    groupId: string
  ): Promise<TournamentPreviewResponse> => {
    try {
      const token = store.getters['settings/getToken'];
      const platformId = store.getters['settings/getPlatformId'];
      const lang = store.getters['settings/getLanguage'];
      const response = await fetch(
        `${ROCKY_URL}/events/getodds/tournament/preview-v3`,
        {
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            platformId: platformId,
            sportId: sportId,
            categoryId: categoryId,
            tournamentId: tournamentId,
            type: 'previewOdds',
            section: 'sport',
            filter: 0,
            lang: lang,
            forceDefaultGameConsole: false,
            mobilePlatform: false,
            groupOddsWithSpreads: true,
            marketId: marketId,
            groupId: groupId
          })
        }
      );

      const data = await response.json();
      return data as TournamentPreviewResponse;
    } catch (error) {
      console.error('Error fetching tournament preview:', error);
      throw error;
    }
  };

  const getMarketGroupsForTournament = async (
    sportId: string | number,
    categoryId: string | number,
    tournamentId: string | number
  ): Promise<MarketGroup[]> => {
    try {
      const response = await fetch(
        `${ROCKY_URL}/settings/sport/groupoddsmarkets/lists-v3`,
        {
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + store.getters['settings/getToken'],
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            section: 'sport',
            lang: store.getters['settings/getLanguage'],
            product_id: 0,
            platform: store.getters['settings/getPlatformId'],
            type: 'groupOdds',
            sportId: sportId,
            tournamentId: tournamentId,
            categoryId: categoryId
          })
        }
      );

      const data = await response.json();
      return data as MarketGroup[];
    } catch (error) {
      console.error('Error fetching market groups for tournament:', error);
      throw error;
    }
  };

  const getOdds = async (): Promise<any> => {
    try {
      const response = await fetch(
        `${ROCKY_URL}/events/getodds/tournament/event-v3`
      );
      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching odds:', error);
      throw error;
    }
  };

  const GetEventDetails = async (
    sportId: string,
    categoryId: string,
    tournamentId: string,
    eventId: string,
    groupId: string
  ): Promise<EventDetailsResponse> => {
    try {
      const token = store.getters['settings/getToken'];
      const platformId = store.getters['settings/getPlatformId'];
      const lang = store.getters['settings/getLanguage'];
      const response = await fetch(
        `${ROCKY_URL}/events/getodds/tournament/event-v3`,
        {
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            platformId: platformId,
            sportId: Number(sportId),
            categoryId: Number(categoryId),
            tournamentId: Number(tournamentId),
            eventId: Number(eventId),
            type: 'groupOdds',
            section: 'sport',
            typegroup: 'detail',
            groupId: groupId,
            lang: lang,
            product_id: 0
          })
        }
      );

      const data: EventDetailsResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching event details:', error);
      throw error;
    }
  };

  const placeBet = async (
    payload: PlaceBetRequest
  ): Promise<PlaceBetResponse> => {
    try {
      const token = store.getters['settings/getToken'];
      const response = await fetch(`${ROCKY_URL}/cart/place-bet`, {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + token,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(errorText || "Errore durante l'invio della scommessa");
      }

      const data: PlaceBetResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Error placing bet:', error);
      throw error;
    }
  };

  /* LIVE */

  const getLiveEventsCountBySport = async (payload: {
    lang?: string;
    sportId?: number;
    sport_id?: string;
  }): Promise<import('../../types/sport').LiveEventsCountBySportResponse> => {
    try {
      const token = store.getters['settings/getToken'];
      const response = await fetch(
        `${ROCKY_URL}/live-events/get-events-count-sport-v3`,
        {
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(
            (() => {
              const body: any = {
                lang: payload.lang || store.getters['settings/getLanguage']
              };
              if (payload.sportId != null) body.sportId = payload.sportId;
              if (payload.sport_id != null) body.sport_id = payload.sport_id;
              return body;
            })()
          )
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          errorText || 'Errore nel recupero conteggio eventi live per sport'
        );
      }

      const data =
        (await response.json()) as import('../../types/sport').LiveEventsCountBySportResponse;
      return data;
    } catch (error) {
      console.error('Error fetching live events count by sport:', error);
      throw error;
    }
  };

  const GetEventDetailsLive = async (
    sportId: string,
    categoryId: string,
    tournamentId: string,
    eventId: string,
    groupId: string | null
  ): Promise<LiveEventDetailsResponse> => {
    try {
      const token = store.getters['settings/getToken'];
      const platformId = store.getters['settings/getPlatformId'];
      const lang = store.getters['settings/getLanguage'];
      const response = await fetch(
        `${ROCKY_URL}/events/getodds/tournament/event-live-v3`,
        {
          method: 'POST',
          headers: {
            Authorization: 'Bearer ' + token,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            platformId: platformId,
            sportId: Number(sportId),
            categoryId: Number(categoryId),
            tournamentId: Number(tournamentId),
            eventId: Number(eventId),
            groupId: groupId,
            type: 'groupOdds',
            section: 'sport',
            typegroup: 'detail',
            lang: lang,
            product_id: 1
          })
        }
      );

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(
          errorText || 'Errore nel recupero dei dettagli evento live'
        );
      }

      const data: LiveEventDetailsResponse = await response.json();
      return data;
    } catch (error) {
      console.error('Error fetching live event details:', error);
      throw error;
    }
  };

  return {
    getSportsFrontV2,
    getSports,
    getCategoriesForSport,
    getTournamentsForSportCategory,
    getFeaturedTournaments,
    getPreviewForTournament,
    getPreviewForTournamentMarket,
    getMarketGroupsForTournament,
    getOdds,
    GetEventDetails,
    GetEventDetailsLive,
    placeBet,
    getLiveEventsCountBySport,
    findEvents,
    getStatsIframes
  };
}
